/**
 * `DataSourceBlock` — one row in the SQL tab's data-sources stepper.
 * Header row carries the step number chip, SQL type chip, editable
 * Name/Key inputs, and the reorder/delete icon cluster. Body is a
 * full-width Monaco SQL editor.
 *
 * Lives inside `reports-ai/` alongside the chat surface because the
 * SQL tab of `/reports/new`'s preview pane hosts the stepper — there
 * is no separate manual-builder route (see ADR-notes in the plan file
 * next to this change).
 */
import { ArrowDown, ArrowUp, X } from "lucide-react";
import { Button, Input } from "@flint/ui";
import { MonacoCodeEditor } from "./monaco-code-editor.tsx";

export interface DataSource {
  key: string;
  name: string;
  type: "sql";
  order: number;
  script: string;
}

interface DataSourceBlockProps {
  block: DataSource;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  /** When true, all inputs go read-only. Used before the report is first saved. */
  readOnly?: boolean;
  onChange: (patch: Partial<DataSource>) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}

export function DataSourceBlock({
  block,
  index,
  isFirst,
  isLast,
  readOnly = false,
  onChange,
  onMove,
  onRemove,
}: DataSourceBlockProps) {
  return (
    <div className="rounded-sm border border-line bg-paper shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-line/60 p-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="inline-flex items-center rounded-sm bg-paper-2 px-2 py-0.5 font-mono text-xs text-muted">
            step {index + 1}
          </span>
          <span className="inline-flex items-center rounded-sm border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wider text-sky-700">
            SQL
          </span>
          <div className="ml-2 flex items-center gap-2">
            <label className="text-xs text-muted">Name</label>
            <Input
              value={block.name}
              onChange={(e) => onChange({ name: e.target.value })}
              placeholder="Rows"
              readOnly={readOnly}
              disabled={readOnly}
              className="h-8 w-36 text-xs"
            />
            <label className="ml-1 text-xs text-muted">Key</label>
            <Input
              value={block.key}
              onChange={(e) => onChange({ key: e.target.value })}
              placeholder="rows"
              readOnly={readOnly}
              disabled={readOnly}
              className="h-8 w-28 font-mono text-xs"
            />
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <Button
            type="button"
            intent="ghost"
            size="sm"
            onClick={() => onMove(-1)}
            disabled={isFirst || readOnly}
            aria-label="Move up"
            className="h-7 w-7 px-0"
          >
            <ArrowUp className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
          </Button>
          <Button
            type="button"
            intent="ghost"
            size="sm"
            onClick={() => onMove(1)}
            disabled={isLast || readOnly}
            aria-label="Move down"
            className="h-7 w-7 px-0"
          >
            <ArrowDown className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
          </Button>
          <Button
            type="button"
            intent="ghost"
            size="sm"
            onClick={onRemove}
            disabled={readOnly}
            aria-label="Delete data source"
            className="h-7 w-7 px-0 hover:bg-red-50 hover:text-red-600"
          >
            <X className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
          </Button>
        </div>
      </div>
      <div className="h-[220px] overflow-hidden bg-[#0f172a]">
        <MonacoCodeEditor
          value={block.script}
          {...(readOnly ? {} : { onChange: (next: string) => onChange({ script: next }) })}
          language="sql"
          ariaLabel={`Data source ${block.name || block.key || `step ${index + 1}`} — SQL`}
        />
      </div>
    </div>
  );
}
